// src/pages/CourseList.jsx
import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import { db } from "./firebase/firebaseConfig";
import { collection, getDocs } from "firebase/firestore";

// ===== Animations =====
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
  100% { transform: translateY(0px); }
`;

// ===== Styled Components =====
const PageContainer = styled.div`
  font-family: "Roboto", sans-serif;
  background: #0d0d0d;
  color: #fff;
  min-height: 100vh;
  padding: 70px 20px;
`;

const Title = styled.h2`
  text-align: center;
  font-size: 3rem;
  margin-bottom: 50px;
  background: linear-gradient(135deg, #ff7a18, #ff0066);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: bold;
`;

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 35px;
  max-width: 1200px;
  margin: auto;
`;

const CourseCard = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border-radius: 18px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.15);

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 10px 25px rgba(255, 122, 24, 0.4);
    animation: ${float} 3s ease-in-out infinite;
  }
`;

const CourseImage = styled.img`
  width: 100%;
  height: 180px;
  object-fit: cover;
`;

const CourseContent = styled.div`
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CourseTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 12px;
  font-weight: 600;
`;

const PriceRow = styled.div`
  margin: 12px 0 22px;
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
`;

const Price = styled.span`
  font-size: 1.6rem;
  font-weight: 700;
  color: #3cff0b;
`;

const OldPrice = styled.span`
  font-size: 1rem;
  text-decoration: line-through;
  color: #aaa;
`;

const Discount = styled.span`
  font-size: 0.9rem;
  background: #ff1744;
  color: #fff;
  padding: 4px 10px;
  border-radius: 6px;
  font-weight: 600;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: auto;
  gap: 12px;
`;

const BuyButton = styled(Link)`
  flex: 1;
  background: linear-gradient(135deg, #ff7a18, #ff0066);
  color: #fff;
  text-align: center;
  padding: 12px;
  border-radius: 10px;
  font-weight: bold;
  text-decoration: none;
  transition: all 0.3s;
  &:hover {
    transform: translateY(-2px);
    background: linear-gradient(135deg, #ff5722, #e91e63);
  }
`;

const DetailsButton = styled(Link)`
  flex: 1;
  background: linear-gradient(135deg, #00d4ff, #00bcd4);
  color: #1e1e2f;
  text-align: center;
  padding: 12px;
  border-radius: 10px;
  font-weight: bold;
  text-decoration: none;
  transition: all 0.3s;
  &:hover {
    transform: translateY(-2px);
    background: linear-gradient(135deg, #0097a7, #00acc1);
  }
`;

// ===== Component =====
const CourseList = () => {
  const [courses, setCourses] = useState([]);

  // Fetch courses from Firestore
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const snap = await getDocs(collection(db, "courses"));
        const courseData = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCourses(courseData);
      } catch (error) {
        console.error("Error fetching courses:", error);
      }
    };
    fetchCourses();
  }, []);

  return (
    <PageContainer>
      <Title>🚀 Available Courses</Title>
      <GridWrapper>
        {courses.length === 0 ? (
          <p style={{ gridColumn: "1 / -1", textAlign: "center" }}>
            No courses available yet
          </p>
        ) : (
          courses.map((course) => {
            const discountedPrice =
              course.discount > 0
                ? (
                    course.price -
                    (course.price * course.discount) / 100
                  ).toFixed(2)
                : course.price;

            return (
              <CourseCard key={course.id}>
                <CourseImage
                  src={course.imageUrl || "https://via.placeholder.com/400x200"}
                  alt={course.title}
                />
                <CourseContent>
                  <CourseTitle>{course.title}</CourseTitle>

                  <PriceRow>
                    <Price>
                      {course.price === 0 ? "Free" : `₹${discountedPrice}`}
                    </Price>
                    {course.discount > 0 && (
                      <>
                        <OldPrice>₹{course.price}</OldPrice>
                        <Discount>{course.discount}% OFF</Discount>
                      </>
                    )}
                  </PriceRow>

                  <ButtonRow>
                    <BuyButton to={`/checkout/${course.id}`}>Buy Now</BuyButton>
                    <DetailsButton to={`/courses/${course.id}`}>
                      View Details
                    </DetailsButton>
                  </ButtonRow>
                </CourseContent>
              </CourseCard>
            );
          })
        )}
      </GridWrapper>
    </PageContainer>
  );
};

export default CourseList;
